import { useCallback, useMemo, useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { useUserWatchlistData } from '../contexts/UserWatchlistDataContext';
import { useUserMovieData } from '../contexts/UserMovieDataContext';
import { useUserProfileCache } from '../contexts/UserProfileCacheContext';
import { useWatchlistPermissions, WatchlistForPermissions } from './useWatchlistPermissions';
import { PrivacyType } from '../types/watchlist';
import { getPrivacyTypeFromLegacy } from '../utils/privacyUtils';
import WatchlistFilteringService from '../services/watchlistFilteringService';
import { getCachedLikedWatchlists } from '../utils/likedWatchlistsCache';
import { formatFirebaseDate } from '../utils/dateUtils';
import { resolveWatchlistLikeCount } from '../utils/watchlistLikeCountResolver';

export interface WatchlistInfo {
  // Basic info
  title: string;
  description: string | null;
  movieCount: number;
  
  // Creator info
  createdBy: string;
  creatorName: string;
  creatorAvatar?: string;
  creatorUid?: string;
  
  // Privacy and permissions
  privacyType: PrivacyType;
  isPrivate: boolean;
  isPublic: boolean;
  isCollaborative: boolean;
  
  // Social info
  collaboratorsCount: number;
  collaboratorProfiles: any[];
  followersCount: number;
  likesCount: number;
  
  // User relationship
  isUserOwnList: boolean;
  isUserCollaborator: boolean;
  isUserLiked: boolean;
  
  // Timestamps
  lastUpdated: string;
  createdAt?: string;
  
  // UI helpers
  displayPrivacyText: string;
  displayCollaboratorText: string;
  displayLikesText: string;
  showPrivacyIndicator: boolean;
  showLikesIndicator: boolean;
  showCollaboratorIndicator: boolean;
}

export interface UseWatchlistInfoOptions {
  watchlistType: string;
  user?: User | null;
  targetUserId?: string;
  isOtherUserWatchlist?: boolean;
  privacyRefreshTrigger?: number;
  
  // For external watchlist data (when not from context)
  externalWatchlistData?: {
    name: string;
    description?: string;
    isPrivate: boolean;
    createdBy: string;
    createdByName: string;
    createdByAvatar?: string;
    collaborators?: string[];
    adminCollaboratorIds?: string[];
    collaboratorProfiles?: any[];
    followerCount?: number;
    movieCount?: number;
    movieDetails?: any[]; // For movie count calculation
    updatedAt?: any;
    createdAt?: any;
  };
}

/**
 * Centralized hook for computing watchlist information consistently
 * across all components (WatchlistDetail, WatchlistWidget, etc.)
 */
export const useWatchlistInfo = (options: UseWatchlistInfoOptions): WatchlistInfo => {
  const {
    watchlistType,
    user,
    targetUserId,
    isOtherUserWatchlist = false,
    externalWatchlistData,
    privacyRefreshTrigger
  } = options;

  const {
    getWatchlistById,
    getDefaultWatchlistSettings
  } = useUserWatchlistData();
  
  const {
    getWatchlistMovieIds,
    movieDataCache
  } = useUserMovieData();
  
  const { getUserDisplayName, getUserAvatar } = useUserProfileCache();

  // State for tracking user's liked watchlists
  const [likedWatchlists, setLikedWatchlists] = useState<Set<string>>(new Set());

  /**
   * Denormalized followerCount on watchlists can drift; for public lists we overlay the count of
   * watchlistLikes documents (same source as the Likes user list).
   */
  const [resolvedLikeCount, setResolvedLikeCount] = useState<number | null>(null);

  // Load user's liked watchlists when user changes (using cache to prevent duplicate API calls)
  useEffect(() => {
    const loadLikedWatchlists = async () => {
      if (!user) {
        setLikedWatchlists(new Set());
        return;
      }

      try {
        // Use the cache utility which deduplicates concurrent requests
        const likedWatchlistsData = await getCachedLikedWatchlists(user.uid);
        const likedIds = likedWatchlistsData.map(watchlist => watchlist.id);
        setLikedWatchlists(new Set(likedIds));
      } catch (error) {
        console.error('useWatchlistInfo: Error loading liked watchlists:', error);
        setLikedWatchlists(new Set());
      }
    };

    loadLikedWatchlists();
  }, [user?.uid]);

  // Helper function to check if user has liked a watchlist
  const isWatchlistLiked = useCallback((watchlistId: string): boolean => {
    return likedWatchlists.has(watchlistId);
  }, [likedWatchlists]);

  // Get watchlist data from context or external source
  const watchlistData = useMemo(() => {
    if (externalWatchlistData) {
      return externalWatchlistData;
    }
    
    return getWatchlistById(watchlistType);
  }, [externalWatchlistData, getWatchlistById, watchlistType, privacyRefreshTrigger]);

  // Overlay query-backed like count for public lists (must run after watchlistData exists — TDZ if placed above useMemo)
  useEffect(() => {
    if (!watchlistData || watchlistData.isPrivate !== false) {
      setResolvedLikeCount(null);
      return;
    }

    let cancelled = false;
    resolveWatchlistLikeCount(watchlistType)
      .then((n) => {
        if (!cancelled) setResolvedLikeCount(n);
      })
      .catch(() => {
        if (!cancelled) setResolvedLikeCount(null);
      });

    return () => {
      cancelled = true;
    };
  }, [watchlistType, watchlistData?.isPrivate, privacyRefreshTrigger]);

  // Calculate movie count for default watchlists with CONSISTENT deduplication
  const calculateMovieCount = useCallback(() => {
    // Priority 1: External watchlist data with explicit movie count
    if (externalWatchlistData?.movieCount !== undefined) {
      return externalWatchlistData.movieCount;
    }
    
    // Priority 2: External watchlist data with movieDetails - apply deduplication
    if (externalWatchlistData?.movieDetails) {
      const processedIds = new Set<string>();
      let validMovieCount = 0;
      
      externalWatchlistData.movieDetails.forEach((movie: any) => {
        const standardizedId = movie.imdbID?.trim() || movie.id?.trim();
        
        if (!standardizedId || standardizedId === 'unknown' || standardizedId === 'N/A' || processedIds.has(standardizedId)) {
          return;
        }
        
        processedIds.add(standardizedId);
        validMovieCount++;
      });
      
      return validMovieCount;
    }
    
    // Priority 3: Context watchlist data with movieDetails - apply deduplication
    if (watchlistData?.movieDetails) {
      const processedIds = new Set<string>();
      let validMovieCount = 0;
      
      watchlistData.movieDetails.forEach((movie: any) => {
        const standardizedId = movie.imdbID?.trim() || movie.id?.trim();
        
        if (!standardizedId || standardizedId === 'unknown' || standardizedId === 'N/A' || processedIds.has(standardizedId)) {
          return;
        }
        
        processedIds.add(standardizedId);
        validMovieCount++;
      });
      
      return validMovieCount;
    }
    
    // Priority 4: Use WatchlistFilteringService for default watchlists only
    if (!isOtherUserWatchlist && user && movieDataCache) {
      return WatchlistFilteringService.getMovieCount(watchlistType, movieDataCache);
    }
    
    return 0;
  }, [
    watchlistData, 
    externalWatchlistData, 
    isOtherUserWatchlist, 
    user, 
    watchlistType, 
    getWatchlistMovieIds, 
    movieDataCache // ✅ Added movieDataCache dependency for consistency
  ]);

  // Create watchlist for permissions hook
  const watchlistForPermissions = useMemo((): WatchlistForPermissions | null => {
    if (!watchlistData) return null;
    
    return {
      id: watchlistType,
      createdBy: watchlistData.createdBy || '',
      createdByName: watchlistData.createdByName || '',
      isPrivate: watchlistData.isPrivate ?? true,
      collaborators: watchlistData.collaborators || [],
      adminCollaboratorIds: (watchlistData as { adminCollaboratorIds?: string[] }).adminCollaboratorIds,
      collaboratorProfiles: watchlistData.collaboratorProfiles || [],
      followerCount: watchlistData.followerCount || 0
    };
  }, [watchlistData, watchlistType]);

  // Get permissions
  const permissions = useWatchlistPermissions(watchlistForPermissions, user || null, isOtherUserWatchlist);

  // Get creator information
  const getCreatorInfo = useCallback(() => {
    if (watchlistData) {
      const creatorUid = watchlistData.createdBy || user?.uid;
      const fallbackName = watchlistData.createdByName || user?.displayName || "You";
      
      return {
        name: creatorUid ? getUserDisplayName(creatorUid, fallbackName) : fallbackName,
        avatar: creatorUid ? (getUserAvatar(creatorUid) || watchlistData.createdByAvatar || user?.photoURL) : (watchlistData.createdByAvatar || user?.photoURL),
        uid: creatorUid
      };
    }
    
    // Fallback for predefined watchlists
    switch (watchlistType) {
      case "main-watchlist":
      case "want-to-watch":
      case "watched":
        return {
          name: user?.displayName || "You",
          avatar: user?.photoURL,
          uid: user?.uid
        };
      case "classics":
        return {
          name: "Film Institute",
          avatar: undefined,
          uid: undefined
        };
      case "villeneuve":
        return {
          name: "Cinema Curator", 
          avatar: undefined,
          uid: undefined
        };
      case "ghibli":
        return {
          name: "Anime Society",
          avatar: undefined,
          uid: undefined
        };
      case "comedy":
        return {
          name: "Comedy Club",
          avatar: undefined,
          uid: undefined
        };
      default:
        return {
          name: user?.uid ? getUserDisplayName(user.uid, user?.displayName || "You") : (user?.displayName || "You"),
          avatar: user?.uid ? (getUserAvatar(user.uid) || user?.photoURL) : user?.photoURL,
          uid: user?.uid
        };
    }
  }, [watchlistData, watchlistType, user, getUserDisplayName, getUserAvatar]);

  // Get watchlist title
  const getWatchlistTitle = useCallback(() => {
    if (watchlistData?.name) {
      return watchlistData.name;
    }
    
    // Fallback for predefined watchlists
    switch (watchlistType) {
      case "want-to-watch":
        return "Want to watch";
      case "watched":
        return "Watched";
      case "main-watchlist":
        if (user?.uid) {
          const displayName = getUserDisplayName(user.uid, user?.displayName || undefined);
          if (displayName && displayName !== 'Unknown User') {
            const firstName = displayName.split(' ')[0];
            return `${firstName}'s watchlist`;
          }
        }
        if (user?.email) {
          const emailName = user.email.split('@')[0];
          const firstName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
          return `${firstName}'s watchlist`;
        } else {
          return "Your watchlist";
        }

      case "classics":
        return "Classics of all time";
      case "villeneuve":
        return "Best of Denis Villeneuve";
      case "ghibli":
        return "Best of Studio Ghibli";
      case "comedy":
        return "Comedy movies";
      default:
        // For unknown watchlist types, don't show "My Watchlist" if data is still loading
        // This prevents the flash of "My Watchlist" before actual content loads
        if (!watchlistData && watchlistType !== 'main-watchlist') {
          return "Loading...";
        }
        return "My Watchlist";
    }
  }, [watchlistData, watchlistType, user, getUserDisplayName]);

  // Format last updated time
  const formatLastUpdated = useCallback(() => {
    if (!watchlistData?.updatedAt) {
      return 'Recently';
    }

    try {
      let updatedDate: Date;
      
      if ((watchlistData.updatedAt as any)?.toDate && typeof (watchlistData.updatedAt as any).toDate === 'function') {
        updatedDate = (watchlistData.updatedAt as any).toDate();
      } else if (typeof watchlistData.updatedAt === 'string') {
        updatedDate = new Date(watchlistData.updatedAt);
      } else if (watchlistData.updatedAt && typeof watchlistData.updatedAt === 'object' && 'getTime' in watchlistData.updatedAt) {
        updatedDate = watchlistData.updatedAt as Date;
      } else {
        return 'Recently';
      }
      
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - updatedDate.getTime());
      const diffMinutes = Math.floor(diffTime / (1000 * 60));
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const diffWeeks = Math.floor(diffDays / 7);

      if (diffMinutes < 1) {
        return 'just now';
      } else if (diffMinutes < 60) {
        return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
      } else if (diffHours < 24) {
        return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
      } else if (diffDays === 1) {
        return '1 day ago';
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else if (diffWeeks === 1) {
        return '1 week ago';
      } else if (diffWeeks < 4) {
        return `${diffWeeks} weeks ago`;
      } else {
        return formatFirebaseDate(updatedDate, undefined, { 
          month: 'short', 
          day: 'numeric',
          year: updatedDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        });
      }
    } catch (error) {
      console.warn('Error formatting date:', error);
      return 'Recently';
    }
  }, [watchlistData]);

  // Compute final watchlist info
  const watchlistInfo = useMemo((): WatchlistInfo => {
    const creatorInfo = getCreatorInfo();
    const title = getWatchlistTitle();
    const lastUpdated = formatLastUpdated();
    
    // Privacy type computation - only count actual collaborators (not the owner)
    const actualCollaborators = (watchlistData?.collaborators || []).filter(uid => uid !== watchlistData?.createdBy);
    const hasCollaborators = actualCollaborators.length > 0;
    const privacyType: PrivacyType = getPrivacyTypeFromLegacy(
      watchlistData?.isPrivate ?? true,
      hasCollaborators
    );
    
    // Counts - use actual collaborators count (excluding owner)
    const collaboratorsCount = actualCollaborators.length;
    const docFollowerCount = watchlistData?.followerCount || 0;
    const effectiveLikeCount =
      resolvedLikeCount !== null ? resolvedLikeCount : docFollowerCount;
    const followersCount = effectiveLikeCount;
    const movieCount = calculateMovieCount();
    
    // User relationships
    const isUserOwnList = permissions.isOwner;
    const isUserCollaborator = permissions.isCollaborator;
    
    // Calculate metrics consistently
    const getDisplayPrivacyText = () => {
      switch (privacyType) {
        case 'private':
          return 'Private';
        case 'collaborative-private':
          return `${collaboratorsCount} collaborator${collaboratorsCount === 1 ? '' : 's'}`;
        case 'public':
        case 'collaborative-public':
          return effectiveLikeCount > 0
            ? `${effectiveLikeCount} like${effectiveLikeCount === 1 ? '' : 's'}`
            : 'Public';
        default:
          return 'Private';
      }
    };

    const getDisplayCollaboratorText = () => {
      // Show individual collaborator names if there are few collaborators, otherwise show count
      if (collaboratorsCount === 0) return '';
      
      if (collaboratorsCount <= 2 && watchlistData?.collaboratorProfiles) {
        const collaboratorNames = watchlistData.collaboratorProfiles
          .filter(profile => profile.uid !== watchlistData.createdBy) // Exclude owner
          .map(profile => profile.name || 'Unknown User')
          .join(', ');
        return collaboratorNames || `${collaboratorsCount} collaborator${collaboratorsCount === 1 ? '' : 's'}`;
      }
      
      return `${collaboratorsCount} collaborator${collaboratorsCount === 1 ? '' : 's'}`;
    };

    const getDisplayLikesText = () => {
      return effectiveLikeCount > 0
        ? `${effectiveLikeCount} like${effectiveLikeCount === 1 ? '' : 's'}`
        : '';
    };

    return {
      // Basic info
      title,
      description: watchlistData?.description || null,
      movieCount,
      
      // Creator info
      createdBy: creatorInfo.uid || '',
      creatorName: creatorInfo.name,
      creatorAvatar: creatorInfo.avatar || undefined,
      creatorUid: creatorInfo.uid,
      
      // Privacy and permissions
      privacyType,
      isPrivate: privacyType === 'private' || privacyType === 'collaborative-private',
      isPublic: privacyType === 'public' || privacyType === 'collaborative-public',
      isCollaborative: privacyType === 'collaborative-private' || privacyType === 'collaborative-public',
      
      // Social info — overlay query-backed count for public lists when available
      collaboratorsCount,
      collaboratorProfiles: watchlistData?.collaboratorProfiles || [],
      followersCount: effectiveLikeCount,
      likesCount: effectiveLikeCount,
      
      // User relationship - Use permissions system for consistency
      isUserOwnList: permissions.isOwner,
      isUserCollaborator: permissions.isCollaborator,
      isUserLiked: user ? isWatchlistLiked(watchlistType) : false,
      
      // Timestamps
      lastUpdated: lastUpdated,
      createdAt: watchlistData?.createdAt,
      
      // UI helpers - consolidated display logic based on permissions
      displayPrivacyText: getDisplayPrivacyText(),
      displayCollaboratorText: getDisplayCollaboratorText(),
      displayLikesText: getDisplayLikesText(),
      showPrivacyIndicator: !(['watched', 'want-to-watch'].includes(watchlistType)),
      showLikesIndicator: permissions.canSeeLikes && (privacyType === 'public' || privacyType === 'collaborative-public'),
      showCollaboratorIndicator: permissions.canSeeCollaborators && collaboratorsCount > 0
    };
  }, [
    watchlistData,
    getWatchlistTitle,
    getCreatorInfo,
    permissions,
    user,
    privacyRefreshTrigger,
    externalWatchlistData,
    isWatchlistLiked,
    watchlistType,
    calculateMovieCount,
    resolvedLikeCount
  ]);

  return watchlistInfo;
}; 